import React, { useCallback, useMemo, useState } from "react";
import { StyleSheet } from "react-native";

import {
  BundleAdditionalVariantFragment,
  type ServiceVariantFragment,
  isNative,
} from "@stepapp/core";
import type { OrderServicePayload, V2CartServicesUpsert } from "@stepapp/core/apiTypes";
import {
  Block,
  BottomSheet,
  Image,
  Typography,
  palette,
  spacing,
  useBreakpoints,
} from "@stepapp/ui";

import OptionRibbon from "./OptionRibbon";
import QuantityActions from "./QuantityActions";
import ServiceActions from "./ServiceActions";

type Params = {
  id: string;
  name: string;
  icon: string;
  description?: Maybe<string>;
  variants: BundleAdditionalVariantFragment[];
  services: V2CartServicesUpsert[];
  setNewServices: (services: (OrderServicePayload | V2CartServicesUpsert)[]) => void;
};

const QuantityAnswerItem = ({
  name,
  description,
  icon,
  id,
  variants,
  services,
  setNewServices,
}: Params) => {
  const [modalVisible, setModalVisible] = useState(false);
  const { mobileOnly, md } = useBreakpoints();

  const singleOption = variants?.length === 1;
  const isFeatured = singleOption && variants[0]?.isFeatured;
  const serviceIndex = services.findIndex((s) => s?.serviceId === id);
  const selectedService = services[serviceIndex];

  const selectedAnswers = useMemo(
    () => services?.find((s) => s?.serviceId === id)?.variants || [],
    [id, services],
  );

  const value = useMemo(
    () => selectedAnswers.reduce((acc, curr) => acc + (curr?.quantity || 0), 0),
    [selectedAnswers],
  );
  const isSelected = value > 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const handleQuantityChange = useCallback(
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO: refactor
    (serviceId: string, variantId: string, quantity: number, singleVariant: boolean) => {
      const oldServices = services.filter((s) => s?.serviceId !== serviceId);
      const serviceToUpdate = services.find((s) => s?.serviceId === serviceId);

      if (singleVariant || !serviceToUpdate) {
        if (quantity === 0) {
          setNewServices(oldServices);
          return;
        }

        setNewServices(oldServices.concat([{ serviceId, variants: [{ quantity, variantId }] }]));
      } else {
        const variantToUpdate = serviceToUpdate?.variants.find((v) => v?.variantId === variantId);

        if (variantToUpdate) {
          const oldVariants = serviceToUpdate?.variants.filter((v) => v?.variantId !== variantId);

          if (quantity === 0 && oldVariants.length === 0) {
            setNewServices(oldServices);
            return;
          }

          const newVariants =
            quantity === 0
              ? oldVariants
              : serviceToUpdate?.variants.map((variant) =>
                  variant?.variantId === variantId ? { ...variant, quantity } : variant,
                );

          const newServices = services.map((service) =>
            service?.serviceId === serviceId ? { ...service, variants: newVariants } : service,
          );

          setNewServices(newServices);
        } else {
          const newServices = services.map((service) =>
            service?.serviceId === serviceId
              ? { ...service, variants: service?.variants.concat([{ quantity, variantId }]) }
              : service,
          );

          setNewServices(newServices);
        }
      }
    },
    [services],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const selectItem = useCallback(
    (value: number) => {
      if (singleOption) {
        handleQuantityChange(id, variants[0]?.id || "", value, singleOption);
      } else {
        setModalVisible(true);
      }
    },
    [singleOption, handleQuantityChange, variants],
  );

  const getAdvancedDescription = useCallback(
    ({
      answers,
      variants,
    }: {
      answers: V2CartServicesUpsert;
      variants: ServiceVariantFragment[];
    }) => {
      const filteredSelections = answers.variants
        .filter((variant) => (variant?.quantity || 0) > 0)
        .map(({ variantId, quantity }) => {
          const selection = variants.find(({ id }) => id === variantId);
          return selection ? `${quantity} x ${selection.title}` : "";
        });

      return filteredSelections.join(", ");
    },
    [],
  );

  const answerDescription =
    variants?.length > 1 && selectedService
      ? getAdvancedDescription({
          variants,
          answers: selectedService,
        }) || description
      : description;

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
  }, []);

  return (
    <>
      <Block
        justifyCenter
        marginBottom={md ? 2.25 : 1}
        paddingLeft={mobileOnly ? 1.75 : 3}
        paddingRight={mobileOnly ? 2.25 : 4.5}
        style={[
          styles.content,
          isSelected && (isNative ? styles.selectedWithoutShadow : styles.selected),
        ]}
      >
        <Block alignCenter row>
          <Block alignCenter justifyCenter style={styles.icon}>
            <Image height={spacing.unit * 5} source={icon} width={spacing.unit * 5} />
          </Block>

          <Block justifyCenter flex={1} marginRight={2.5}>
            <Typography
              ellipsizeMode="tail"
              numberOfLines={1}
              type={isSelected ? "white" : "primary"}
              variant="answerTitle"
            >
              {name}
            </Typography>

            {!!answerDescription && (
              <Block marginTop={0.5}>
                <Typography
                  ellipsizeMode="tail"
                  numberOfLines={1}
                  type={isSelected ? "white" : "secondary"}
                  variant="lightLabel"
                >
                  {answerDescription}
                </Typography>
              </Block>
            )}
          </Block>

          <QuantityActions
            onChange={selectItem}
            quantity={value}
            singleOption={singleOption}
            textColor="white"
          />
        </Block>
        {isFeatured && (
          <OptionRibbon
            placement="left"
            transform={[{ rotate: "-45deg" }, { translateX: -24 }, { translateY: -48 }]}
          />
        )}
      </Block>

      <BottomSheet
        contentStyle={styles.actionsContainer}
        toggle={handleCloseModal}
        visible={modalVisible}
      >
        <ServiceActions
          serviceId={id}
          closeModal={handleCloseModal}
          handleQuantityChange={handleQuantityChange}
          variants={variants}
          selectedAnswers={selectedAnswers}
          title={name}
        />
      </BottomSheet>
    </>
  );
};

export default React.memo(QuantityAnswerItem);

const styles = StyleSheet.create({
  actionsContainer: {
    paddingBottom: spacing.unit * 4,
    padding: spacing.unit * 2.5,
  },
  content: {
    backgroundColor: palette.common.lightGray,
    borderRadius: spacing.radius,
    overflow: "hidden",
    paddingVertical: spacing.unit * 2,
  },
  icon: {
    borderRadius: 50,
    height: 42,
    marginRight: 16,
    width: 42,
  },
  selected: {
    backgroundColor: palette.primary.accent,
    boxShadow:
      "0px 100px 80px rgba(0, 0, 84, 0.07), 0px 41.7776px 33.4221px rgba(0, 0, 84, 0.0503198), 0px 22.3363px 17.869px rgba(0, 0, 84, 0.0417275), 0px 12.5216px 10.0172px rgba(0, 0, 84, 0.035), 0px 6.6501px 5.32008px rgba(0, 0, 84, 0.0282725), 0px 2.76726px 2.21381px rgba(0, 0, 84, 0.0196802)",
  },
  selectedWithoutShadow: {
    backgroundColor: palette.primary.accent,
  },
});
